/**
 * Mobile app layer (snippets/app-shell.liquid).
 *
 *   Lumi.app.platform              'ios' | 'android' | 'desktop' (read again at click time)
 *   Lumi.app.standalone            true when launched from the home screen
 *   Lumi.app.installable           true once the browser offered an install prompt
 *   Lumi.app.promptInstall()       native prompt, the iOS steps sheet, or generic steps
 *   Lumi.app.enableSheetSwipe(panel, { onClose, media })   swipe down to dismiss a bottom sheet
 *   Lumi.app.ptrProgress(px)       0..1 eased pull to refresh progress (1 at the threshold)
 *
 * Tab bar: shrinks to a slim icon strip while scrolling and returns to full size when scrolling stops, keeps the
 * wishlist and cart counts, opens search and the cart drawer.
 * Pull to refresh only runs in standalone display mode. No service worker (out of theme scope).
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});
  if (Lumi.app && Lumi.app.ready) return;

  const root = document.documentElement;
  const mobile = window.matchMedia('(max-width: 989px)');
  const reduced = () => (Lumi.reducedMotion ? Lumi.reducedMotion() : window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  function detectPlatform() {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'desktop';
  }

  const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (standalone) root.dataset.standalone = 'true';

  /* ======================================================================
   * Swipe down to dismiss bottom sheets
   * ==================================================================== */
  const swipeBound = new WeakSet();

  // The nearest element between the touch target and the panel that can scroll vertically.
  function scrollParent(node, panel) {
    let el = node;
    while (el && el !== panel.parentElement) {
      if (el.nodeType === 1) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      }
      if (el === panel) break;
      el = el.parentElement;
    }
    return null;
  }

  function enableSheetSwipe(panel, options = {}) {
    if (!panel || swipeBound.has(panel)) return;
    swipeBound.add(panel);
    const query = window.matchMedia(options.media || '(max-width: 989px)');
    const dialog = panel.closest('dialog') || panel;
    let drag = null;

    const reset = () => {
      panel.style.removeProperty('transform');
      panel.style.removeProperty('transition');
      panel.style.removeProperty('--sheet-drag');
      dialog.removeAttribute('data-sheet-dragging');
    };
    dialog.addEventListener('close', reset);

    panel.addEventListener(
      'touchstart',
      (e) => {
        if (!query.matches || e.touches.length !== 1 || !dialog.open) return;
        if (e.target.closest('input[type="range"], [data-no-sheet-swipe]')) return;
        const t = e.touches[0];
        const scroller = scrollParent(e.target, panel);
        drag = {
          x: t.clientX,
          y: t.clientY,
          dy: 0,
          height: panel.getBoundingClientRect().height || window.innerHeight,
          scroller,
          atTop: !scroller || scroller.scrollTop <= 0,
          decided: false,
          active: false,
          samples: [{ y: t.clientY, t: e.timeStamp }],
        };
      },
      { passive: true }
    );

    panel.addEventListener(
      'touchmove',
      (e) => {
        if (!drag) return;
        const t = e.touches[0];
        const dx = t.clientX - drag.x;
        const dy = t.clientY - drag.y;
        if (!drag.decided) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          drag.decided = true;
          const scrolledAway = drag.scroller && drag.scroller.scrollTop > 0;
          if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || !drag.atTop || scrolledAway) {
            drag = null;
            return;
          }
          drag.active = true;
          drag.y = t.clientY; // start from here so the panel does not jump by the slop
          dialog.setAttribute('data-sheet-dragging', '');
          panel.style.transition = 'none';
        }
        if (!drag.active) return;
        e.preventDefault();
        drag.dy = Math.max(0, t.clientY - drag.y);
        drag.samples.push({ y: t.clientY, t: e.timeStamp });
        if (drag.samples.length > 6) drag.samples.shift();
        panel.style.transform = `translate3d(0, ${drag.dy}px, 0)`;
        panel.style.setProperty('--sheet-drag', clamp(drag.dy / drag.height, 0, 1).toFixed(3));
      },
      { passive: false }
    );

    const end = (e) => {
      if (!drag) return;
      const state = drag;
      drag = null;
      if (!state.active) return;
      const first = state.samples[0];
      const last = state.samples[state.samples.length - 1];
      const dt = Math.max(1, (e ? e.timeStamp : last.t) - first.t);
      const velocity = (last.y - first.y) / dt; // px per ms, positive is downward
      const shouldClose = state.dy > state.height * 0.25 || (velocity > 0.55 && state.dy > 24);
      if (shouldClose) {
        const ms = reduced() ? 0 : Math.round(clamp((state.height - state.dy) / 2.2, 140, 300));
        panel.style.transition = ms ? `transform ${ms}ms cubic-bezier(0.2, 0.7, 0.1, 1)` : 'none';
        panel.style.transform = `translate3d(0, ${Math.ceil(state.height + 24)}px, 0)`;
        panel.style.setProperty('--sheet-drag', '1');
        if (typeof options.onClose === 'function') options.onClose();
        // Safety net: if the owner kept the sheet open, bring it back.
        setTimeout(() => {
          if (dialog.open && !dialog.hasAttribute('data-closing') && !dialog.classList.contains('is-closing')) springBack();
        }, 900);
      } else {
        springBack();
      }
    };

    const springBack = () => {
      panel.style.transition = reduced() ? 'none' : 'transform 0.42s cubic-bezier(0.2, 0.9, 0.2, 1.05)';
      panel.style.transform = 'translate3d(0, 0, 0)';
      panel.style.setProperty('--sheet-drag', '0');
      const done = () => {
        panel.removeEventListener('transitionend', done);
        if (!drag) reset();
      };
      if (reduced()) done();
      else {
        panel.addEventListener('transitionend', done);
        setTimeout(done, 500);
      }
    };

    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', () => end());
  }

  /* ======================================================================
   * Pull to refresh (standalone only)
   * ==================================================================== */
  const PTR_THRESHOLD = 80;
  const PTR_MAX = 140;

  // Finger travel to indicator distance: 1:1 at first, then rubber band towards PTR_MAX.
  function ptrResist(raw) {
    if (!(raw > 0)) return 0;
    return PTR_MAX * (1 - Math.exp(-raw / PTR_MAX));
  }

  // Indicator distance to eased progress: 0 at rest, 1 at the threshold and beyond.
  function ptrProgress(distance) {
    const p = clamp((Number(distance) || 0) / PTR_THRESHOLD, 0, 1);
    return 1 - (1 - p) * (1 - p);
  }

  function setupPullToRefresh() {
    const template = document.querySelector('template[data-ptr-template]');
    if (!template || document.querySelector('[data-ptr]')) return;
    document.body.appendChild(template.content.cloneNode(true));
    const el = document.querySelector('[data-ptr]');
    const hint = el.querySelector('[data-ptr-hint]');
    let pull = null;
    let busy = false;

    const setState = (state) => {
      if (el.dataset.state === state) return;
      el.dataset.state = state;
      const label = state === 'armed' ? el.dataset.labelRelease : state === 'loading' ? el.dataset.labelLoading : el.dataset.labelPull;
      if (hint && label) hint.textContent = label;
    };

    const render = (distance) => {
      el.style.setProperty('--ptr-y', `${distance.toFixed(1)}px`);
      el.style.setProperty('--ptr-p', ptrProgress(distance).toFixed(3));
    };

    const blocked = (target) =>
      busy ||
      window.scrollY > 0 ||
      root.classList.contains('is-locked') ||
      document.querySelector('dialog[open]') ||
      (target.closest && target.closest('input, textarea, select, [contenteditable], [data-no-ptr]'));

    // The pull needs a non-passive touchmove (to hold the page still while pulling). A non-passive
    // listener makes the browser wait for script before every scroll, so it is only attached for a
    // gesture that starts at the very top of the page and removed as soon as it is not a pull.
    const onMove = (e) => {
      if (!pull) return;
      const t = e.touches[0];
      const dx = t.clientX - pull.x;
      const dy = t.clientY - pull.y;
      if (!pull.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        pull.decided = true;
        // Horizontal swipes (galleries, reels) and upward scrolls are left alone.
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || window.scrollY > 0) {
          pull = null;
          document.removeEventListener('touchmove', onMove, { passive: false });
          return;
        }
        pull.active = true;
        pull.y = t.clientY;
      }
      if (!pull.active) return;
      if (e.cancelable) e.preventDefault();
      pull.distance = ptrResist(t.clientY - pull.y);
      const armed = pull.distance >= PTR_THRESHOLD;
      if (armed && !pull.armed && navigator.vibrate) navigator.vibrate(8);
      pull.armed = armed;
      setState(armed ? 'armed' : 'pulling');
      render(pull.distance);
    };

    document.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1 || blocked(e.target)) return;
        const t = e.touches[0];
        pull = { x: t.clientX, y: t.clientY, decided: false, active: false, distance: 0, armed: false };
        document.addEventListener('touchmove', onMove, { passive: false });
      },
      { passive: true }
    );

    const release = () => {
      document.removeEventListener('touchmove', onMove, { passive: false });
      if (!pull) return;
      const state = pull;
      pull = null;
      if (!state.active) return;
      if (state.distance >= PTR_THRESHOLD) {
        busy = true;
        setState('loading');
        render(PTR_THRESHOLD);
        if (Lumi.announce) Lumi.announce(el.dataset.labelLoading);
        setTimeout(() => window.location.reload(), reduced() ? 120 : 760);
      } else {
        setState('settling');
        render(0);
        setTimeout(() => {
          if (!pull && el.dataset.state === 'settling') setState('idle');
        }, reduced() ? 0 : 430);
      }
    };

    document.addEventListener('touchend', release);
    document.addEventListener('touchcancel', release);
  }

  /* ======================================================================
   * Tab bar
   * ==================================================================== */
  function setupTabBar() {
    const bar = document.querySelector('[data-tab-bar]');
    if (!bar) return;
    const wishLink = bar.querySelector('[data-tab-wishlist]');
    const cartLink = bar.querySelector('[data-tab-cart]');
    let cartCount = parseInt(bar.dataset.cartCountInitial, 10) || 0;

    // While the page is scrolling the bar shrinks to a slim icon strip; when scrolling stops it
    // returns to full size. It never leaves the screen, so nothing else on the page has to move.
    const setCompact = (compact) => {
      const value = String(!!compact);
      if (bar.dataset.compact === value) return;
      bar.dataset.compact = value;
    };
    bar.dataset.compact = 'false';
    root.dataset.tabBarState = 'shown';

    let idleTimer = 0;
    let lastY = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        if (!mobile.matches) return;
        const y = Math.max(0, window.scrollY);
        const moved = Math.abs(y - lastY);
        lastY = y;
        if (moved > 2 && y > 24) setCompact(true);
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => setCompact(false), 220);
      },
      { passive: true }
    );
    mobile.addEventListener('change', () => setCompact(false));
    bar.addEventListener('focusin', () => setCompact(false));

    const label = (link, count) => {
      if (!link) return;
      if (!count) link.removeAttribute('aria-label');
      else {
        const text = count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
        if (text) link.setAttribute('aria-label', text.replace(/\{\{\s*count\s*\}\}/, count));
      }
    };

    const paintCount = (name, count, bump) => {
      const badge = bar.querySelector(`[data-tab-count="${name}"]`);
      if (!badge) return;
      badge.textContent = count;
      badge.hidden = !count;
      if (bump && count && !reduced()) {
        badge.removeAttribute('data-bump');
        void badge.offsetWidth;
        badge.setAttribute('data-bump', '');
      }
    };

    const wishlistHandles = (event) => {
      if (event && event.detail && Array.isArray(event.detail.handles)) return event.detail.handles;
      if (Lumi.wishlist && Lumi.wishlist.get) return Lumi.wishlist.get();
      return (Lumi.storage && Lumi.storage.get('lumi:wishlist', [])) || [];
    };
    const onWishlist = (event) => {
      const list = wishlistHandles(event);
      const count = Array.isArray(list) ? list.length : 0;
      paintCount('wishlist', count, !!event);
      label(wishLink, count);
    };
    if (wishLink) {
      Lumi.on('lumi:wishlist:changed', onWishlist);
      onWishlist();
    }

    // Cart count, plus a short vibration when an add to cart succeeds.
    Lumi.on('lumi:cart:updated', (event) => {
      const cart = event.detail && event.detail.cart;
      if (!cart || typeof cart.item_count !== 'number') return;
      const grew = cart.item_count > cartCount;
      cartCount = cart.item_count;
      paintCount('cart', cartCount, grew);
      label(cartLink, cartCount);
      if (grew && navigator.vibrate) {
        try {
          navigator.vibrate(12);
        } catch (e) {
          /* vibration blocked: no feedback, nothing else to do */
        }
      }
    });

    if (cartLink) {
      cartLink.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        const drawer = Lumi.settings && Lumi.settings.cartType === 'drawer' && customElements.get('cart-drawer');
        const onCartPage = window.location.pathname.replace(/\/$/, '') === ((Lumi.routes && Lumi.routes.cart_url) || '/cart').replace(/\/$/, '');
        if (!drawer || onCartPage) return;
        event.preventDefault();
        Lumi.emit('lumi:cart:open');
      });
    }
  }

  /* ======================================================================
   * Install
   * ==================================================================== */
  let deferredPrompt = null;

  function installButtons() {
    return document.querySelectorAll('[data-app-install]');
  }

  function refreshInstallButtons() {
    const show = !app.standalone && !app.installed && (!!deferredPrompt || app.platform === 'ios' || mobile.matches);
    installButtons().forEach((btn) => {
      btn.hidden = !show;
    });
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    app.installable = true;
    refreshInstallButtons();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    app.installable = false;
    app.installed = true;
    refreshInstallButtons();
    closeSheet();
  });

  let sheetTrigger = null;

  function sheetEl() {
    return document.querySelector('[data-install-sheet]');
  }

  function openSheet(variant, trigger) {
    const sheet = sheetEl();
    if (!sheet) return;
    const title = sheet.querySelector('#AppInstallTitle');
    sheet.dataset.platform = variant;
    if (title) title.textContent = variant === 'ios' ? title.dataset.titleIos : title.dataset.titleGeneric;
    sheet.querySelectorAll('[data-steps]').forEach((list) => {
      list.hidden = list.dataset.steps !== variant;
    });
    if (sheet.open) return;
    sheetTrigger = trigger || document.activeElement;
    sheet.removeAttribute('data-closing');
    sheet.showModal();
    if (Lumi.lockScroll) Lumi.lockScroll(true);
    const close = sheet.querySelector('.app-sheet__close');
    if (close) close.focus({ preventScroll: true });
    enableSheetSwipe(sheet, { media: '(max-width: 749px)', onClose: () => closeSheet(true) });
    if (Lumi.emit) Lumi.emit('lumi:dialog:opened', { id: sheet.id });
  }

  function closeSheet(fromSwipe) {
    const sheet = sheetEl();
    if (!sheet || !sheet.open || sheet.hasAttribute('data-closing')) return;
    if (fromSwipe || reduced()) {
      setTimeout(() => sheet.close(), fromSwipe && !reduced() ? 240 : 0);
      sheet.setAttribute('data-closing', '');
      sheet.style.animation = 'none';
      return;
    }
    sheet.setAttribute('data-closing', '');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sheet.close();
    };
    sheet.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 320);
  }

  function setupSheet() {
    const sheet = sheetEl();
    if (!sheet) return;
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSheet();
    });
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet || event.target.closest('[data-install-close]')) closeSheet();
    });
    sheet.addEventListener('keydown', (event) => {
      if (Lumi.trapFocus) Lumi.trapFocus(sheet, event);
    });
    sheet.addEventListener('close', () => {
      sheet.removeAttribute('data-closing');
      sheet.style.removeProperty('animation');
      if (Lumi.lockScroll && !document.querySelector('dialog[open]')) Lumi.lockScroll(false);
      if (sheetTrigger && sheetTrigger.isConnected && sheetTrigger.offsetParent !== null) sheetTrigger.focus({ preventScroll: true });
      sheetTrigger = null;
      if (Lumi.emit) Lumi.emit('lumi:dialog:closed', { id: sheet.id });
    });
  }

  async function promptInstall(trigger) {
    if (app.platform === 'ios') {
      openSheet('ios', trigger);
      return 'instructions';
    }
    if (deferredPrompt) {
      const promptEvent = deferredPrompt;
      deferredPrompt = null;
      app.installable = false;
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        refreshInstallButtons();
        return choice && choice.outcome;
      } catch (e) {
        openSheet('generic', trigger);
        return 'instructions';
      }
    }
    openSheet('generic', trigger);
    return 'instructions';
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-app-install]');
    if (!btn) return;
    event.preventDefault();
    // The menu drawer is a modal too: close it first so the sheet is not stacked under it.
    const menu = btn.closest('menu-drawer');
    const menuDialog = menu && menu.querySelector('dialog');
    if (menuDialog && menuDialog.open && typeof menu.close === 'function') {
      // Wait for the menu's own close handling (focus return, scroll unlock) before opening.
      menuDialog.addEventListener('close', () => promptInstall(menu.querySelector('[data-drawer-open]')), { once: true });
      menu.close(true);
    } else {
      promptInstall(btn);
    }
  });

  /* ======================================================================
   * Public API and start
   * ==================================================================== */
  const app = (Lumi.app = Object.assign(Lumi.app || {}, {
    ready: true,
    platform: detectPlatform(),
    standalone,
    installable: false,
    installed: false,
    promptInstall,
    openInstallSheet: openSheet,
    closeInstallSheet: closeSheet,
    enableSheetSwipe,
    ptrProgress,
    ptrResist,
    ptrThreshold: PTR_THRESHOLD,
  }));

  function start() {
    setupTabBar();
    setupSheet();
    refreshInstallButtons();
    mobile.addEventListener('change', refreshInstallButtons);
    if (standalone) setupPullToRefresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
